import { Injectable } from '@angular/core';
import {
  FlightStatusErrorMessages,
  FlightStatusForm
} from '@com-delta-idp/mnx-capability-flight-status-ui';
import { FlightStatusAem } from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { MessageBannerOptions } from '@com-delta-omnichannel/mach-core-js';
import {
  EMPTY_FORM_STRUCTURE,
  FLIGHT_STATUS_SMART_CONSTANTS
} from '../models/constants/flight-status.constants';

@Injectable({ providedIn: 'root' })
export class FlightStatusAdapterService {
  protected errorBannerMessage = '';
  readonly messageBannerId = FLIGHT_STATUS_SMART_CONSTANTS.IDS.ERROR_BANNER;

  getContent(flightStatusAem: FlightStatusAem | null): FlightStatusForm | null {
    if (!flightStatusAem) {
      this.errorBannerMessage =
        FLIGHT_STATUS_SMART_CONSTANTS.ERROR_MESSAGES.AEM_CONTENT_UNAVAILABLE;
      return null;
    }

    this.errorBannerMessage =
      flightStatusAem.error?.errorBannerMessage ||
      FLIGHT_STATUS_SMART_CONSTANTS.ERROR_MESSAGES.AEM_CONTENT_UNAVAILABLE;

    return {
      inputFields: this.getInputFields(flightStatusAem),
      errorMessages: this.getErrorMessages(flightStatusAem),
      orLabel: flightStatusAem.orLabel,
      ...EMPTY_FORM_STRUCTURE
    };
  }

  private getInputFields(fs: FlightStatusAem): FlightStatusForm['inputFields'] {
    const { LABELS, PLACEHOLDERS } = FLIGHT_STATUS_SMART_CONSTANTS;
    const co = <T>(v: T | null | undefined, d: T): T => v ?? d;

    return {
      departureDate: {
        title: co(fs.searchByLabel, LABELS.SEARCH_BY_DATE),
        placeHolder: co(fs.searchByLabelFloat, LABELS.SELECT_DATE),
        floatingLabel: false
      },
      flightNumber: {
        title: co(fs.flightNumberLabelFloat, LABELS.FLIGHT_NUMBER),
        placeHolder: co(fs.flightNumber, PLACEHOLDERS.FLIGHT_NUMBER_EXAMPLE),
        floatingLabel: true
      },
      multiRoutePicker: {
        origin: {
          title: co(fs.fromLabel, LABELS.FROM),
          subtitle: co(fs.fromLabelFloat, LABELS.FROM_AIRPORT)
        },
        destination: {
          title: co(fs.toLabel, LABELS.TO),
          subtitle: co(fs.toLabelFloat, LABELS.TO_AIRPORT)
        },
        ariaLabels: {
          origin: co(fs.fromLabelFloat, LABELS.FROM_AIRPORT),
          swapArrow: fs.accessibility.swapAirportsAriaLabel,
          departure: co(fs.toLabelFloat, LABELS.TO_AIRPORT)
        }
      }
    };
  }

  private getErrorMessages(fs: FlightStatusAem): FlightStatusErrorMessages {
    if (!fs.errorMessages) return {};

    const title = fs.errorText?.title || '';
    const { flightNumber, departureDate, fromCity, toCity, sameAirport } =
      fs.errorMessages;

    return {
      FLIGHT_NUM_REQD: { title, description: flightNumber.required },
      FLIGHT_DATE_REQD: { title, description: departureDate.required },
      FROM_CITY_REQD: { title, description: fromCity.required },
      TO_CITY_REQD: { title, description: toCity.required },
      INVALID_FROMTO: { title, description: sameAirport.invalid }
    };
  }

  setErrorBanner(): MessageBannerOptions {
    return {
      id: this.messageBannerId,
      type: 'error',
      isSticky: false,
      hasBar: true,
      iconSize: 'large',
      content: {
        header: { title: '', hasCloseButton: false },
        description: this.errorBannerMessage
      }
    };
  }

  getErrorBannerMessage(): string {
    return this.errorBannerMessage;
  }

  setErrorMessage(message: string): void {
    this.errorBannerMessage = message;
  }
}
